/**
 * Focus score below which a photo is flagged as possibly blurred. Calibrated on 4K frames from the
 * video stream; raise it if blurred photos slip through, lower it if sharp ones get flagged.
 */
export const SHARPNESS_THRESHOLD = 50;

const GRID_COLUMNS = 4;
const GRID_ROWS = 3;
const TILE_SIZE = 256;

/** Variance of the Laplacian of a square RGBA tile; edges score high, blur and flat areas low. */
export function getLaplacianVariance(rgba: Uint8ClampedArray, size: number): number {
  const gray = new Float32Array(size * size);
  for (let i = 0, p = 0; i < gray.length; i++, p += 4) {
    gray[i] = 0.299 * rgba[p] + 0.587 * rgba[p + 1] + 0.114 * rgba[p + 2];
  }
  let sum = 0;
  let sumOfSquares = 0;
  let count = 0;
  for (let y = 1; y < size - 1; y++) {
    for (let x = 1; x < size - 1; x++) {
      const i = y * size + x;
      const value = 4 * gray[i] - gray[i - 1] - gray[i + 1] - gray[i - size] - gray[i + size];
      sum += value;
      sumOfSquares += value * value;
      count++;
    }
  }
  if (!count) return 0;
  const mean = sum / count;
  return sumOfSquares / count - mean * mean;
}

/**
 * Rough focus score of a captured frame. Scores a grid of tiles at full resolution and takes the
 * second-sharpest, so one smooth body panel does not count as blur and one noisy corner does not
 * count as sharp. Returns Infinity when the frame cannot be read, so nothing gets flagged.
 */
export function measureSharpness(canvas: HTMLCanvasElement): number {
  const ctx = canvas.getContext('2d');
  const tile = Math.min(
    TILE_SIZE,
    Math.floor(canvas.width / GRID_COLUMNS),
    Math.floor(canvas.height / GRID_ROWS),
  );
  if (!ctx || tile < 16) return Infinity;

  const scores: number[] = [];
  for (let row = 0; row < GRID_ROWS; row++) {
    for (let column = 0; column < GRID_COLUMNS; column++) {
      const x = Math.floor(((column + 0.5) * canvas.width) / GRID_COLUMNS - tile / 2);
      const y = Math.floor(((row + 0.5) * canvas.height) / GRID_ROWS - tile / 2);
      scores.push(getLaplacianVariance(ctx.getImageData(x, y, tile, tile).data, tile));
    }
  }
  scores.sort((a, b) => b - a);
  return scores[1];
}
